import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { cn } from '@/lib/cn';
import { formatInt, formatNumber, formatPct } from '@/lib/format';
import { buildReelUrl } from '@/lib/ig';
import type { AnalyticsInsightModel } from '@/lib/analyticsInsights';
import type { AnalyticsInsightAccount, AnalyticsInsightsResponse } from '@/types/api';

const PAGE_SIZE = 8;
type EvidenceView = 'selected' | 'all' | 'reels';

export function InsightEvidence({
  model,
  data,
}: {
  model: AnalyticsInsightModel;
  data: AnalyticsInsightsResponse;
}) {
  const { t, locale } = useI18n();
  const [view, setView] = useState<EvidenceView>(model.selected.length ? 'selected' : 'all');
  const [page, setPage] = useState(0);
  const allAccounts = [...model.accounts, ...model.excludedAccounts];
  const accounts = view === 'selected' ? model.selected : allAccounts;
  const selectedIds = new Set(model.selected.map((account) => account.accountId));
  const reels = data.topReels.filter((reel) => selectedIds.has(reel.accountId));
  const totalRows = view === 'reels' ? reels.length : accounts.length;
  const pages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
  const currentPage = Math.min(page, pages - 1);
  const start = currentPage * PAGE_SIZE;
  const displayedAccounts = accounts.slice(start, start + PAGE_SIZE);
  const displayedReels = reels.slice(start, start + PAGE_SIZE);
  const reelViews = reels.reduce((total, reel) => total + reel.viewsDelta, 0);
  const selectView = (next: EvidenceView) => {
    setView(next);
    setPage(0);
  };
  return (
    <div className="min-w-0 overflow-hidden rounded-[14px] border border-line-1 bg-bg-1">
      <div
        className="flex flex-wrap items-center gap-x-5 border-b border-line-1 px-4 sm:px-5"
        aria-label={t('Détail des contributeurs', 'Contributor details')}
      >
        {(
          [
            ['selected', t('Comptes sélectionnés', 'Selected accounts')],
            ['all', t('Tous les comptes', 'All accounts')],
            ['reels', t('Reels contributeurs', 'Contributing Reels')],
          ] as const
        ).map(([key, label]) => (
          <button
            type="button"
            key={key}
            aria-pressed={view === key}
            disabled={key !== 'all' && !model.selected.length}
            onClick={() => selectView(key)}
            className={cn(
              '-mb-px min-h-11 border-b-2 py-3 text-[12px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-violet disabled:opacity-40',
              view === key
                ? 'border-violet text-text-1'
                : 'border-transparent text-text-3 hover:text-text-1',
            )}
          >
            {label}
            {key === 'selected' && (
              <span className="ml-2 tabular-nums text-text-3">
                {formatInt(model.selected.length)}
              </span>
            )}
          </button>
        ))}
      </div>
      {view === 'reels' && (
        <div className="border-b border-line-1 bg-bg-2 px-4 py-3 text-[12px] leading-5 text-text-3 sm:px-5">
          <p>
            {t(
              `${reels.length} Reels de la sélection présents dans les ${data.topReels.length} meilleurs Reels disponibles (limite : ${data.topReelLimit}).`,
              `${reels.length} selected-account Reels among the ${data.topReels.length} top Reels available (limit: ${data.topReelLimit}).`,
            )}
          </p>
          <p>
            {t(
              `Ce détail couvre ${formatPct(model.selectedViews > 0 ? reelViews / model.selectedViews : null, { sign: false, digits: 1 })} des vues de la sélection ; les analyses utilisent tous les Reels mesurés.`,
              `These details cover ${formatPct(model.selectedViews > 0 ? reelViews / model.selectedViews : null, { sign: false, digits: 1 })} of selected-account views; analyses use all measured Reels.`,
            )}
          </p>
        </div>
      )}
      {totalRows === 0 ? (
        <p className="p-5 text-[13px] text-text-3">
          {view === 'reels'
            ? t(
                'Aucun Reel de ces comptes ne figure dans le détail disponible.',
                'No Reel from these accounts is included in the available details.',
              )
            : t('Aucun compte dans cette sélection.', 'No accounts in this selection.')}
        </p>
      ) : (
        <table
          className="w-full table-fixed text-[12px]"
          aria-label={
            view === 'reels'
              ? t('Reels de la sélection', 'Selected-account Reels')
              : t('Comptes et part des vues gagnées', 'Accounts and share of views gained')
          }
        >
          <colgroup>
            <col className="w-[50%]" />
            <col className="w-[23%]" />
            <col className="w-[27%]" />
          </colgroup>
          <thead className="text-[11px] text-text-3">
            <tr className="border-b border-line-1">
              <th scope="col" className="px-3 py-3 text-left font-normal sm:px-5">
                {view === 'reels' ? t('Reel / compte', 'Reel / account') : t('Compte', 'Account')}
              </th>
              <th scope="col" className="px-2 py-3 text-right font-normal sm:px-5">
                {t('Vues gagnées', 'Views gained')}
              </th>
              <th scope="col" className="px-3 py-3 text-right font-normal sm:px-5">
                {t('Part du total', 'Share of total')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line-1">
            {view === 'reels'
              ? displayedReels.map((reel) => (
                  <tr key={`${reel.accountId}:${reel.reelId}`}>
                    <td className="min-w-0 px-3 py-2.5 sm:px-5">
                      {reel.shortcode ? (
                        <a
                          href={buildReelUrl(reel.shortcode)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex min-h-11 items-center gap-1.5 rounded font-medium text-text-1 hover:text-violet-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet"
                        >
                          <span className="min-w-0 truncate">{reel.shortcode}</span>
                          <ArrowUpRight className="size-3.5 shrink-0" aria-hidden="true" />
                          <span className="sr-only">
                            {t(
                              'Ouvrir sur Instagram dans un nouvel onglet',
                              'Open on Instagram in a new tab',
                            )}
                          </span>
                        </a>
                      ) : (
                        <span className="block text-text-3">
                          {t('Reel sans lien', 'Reel link unavailable')}
                        </span>
                      )}
                      <Link
                        className="block truncate rounded py-1 text-[11px] text-text-3 hover:text-violet-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet"
                        to={`/accounts/${encodeURIComponent(reel.accountId)}`}
                      >
                        @{reel.username}
                      </Link>
                      {reel.publishedAt && (
                        <span className="block text-[10px] text-text-3">
                          {new Date(reel.publishedAt).toLocaleDateString(locale, {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            timeZone: 'UTC',
                          })}
                        </span>
                      )}
                    </td>
                    <td
                      className="px-2 py-3 text-right font-medium tabular-nums sm:px-5"
                      title={formatInt(reel.viewsDelta)}
                    >
                      {formatNumber(reel.viewsDelta)}
                    </td>
                    <td className="px-3 py-3 sm:px-5">
                      <Contribution
                        value={model.totalViews > 0 ? reel.viewsDelta / model.totalViews : null}
                      />
                    </td>
                  </tr>
                ))
              : displayedAccounts.map((account) => (
                  <AccountRow
                    key={account.accountId}
                    account={account}
                    totalViews={model.totalViews}
                  />
                ))}
          </tbody>
        </table>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line-1 px-3 py-2 text-[11px] text-text-3 sm:px-5">
        <span aria-live="polite">
          {totalRows > 0
            ? `${formatInt(start + 1)}–${formatInt(Math.min(start + PAGE_SIZE, totalRows))} / ${formatInt(totalRows)}`
            : '0'}{' '}
          {view === 'reels' ? t('Reels', 'Reels') : t('comptes', 'accounts')}
        </span>
        {pages > 1 && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label={t('Page précédente', 'Previous page')}
              disabled={currentPage === 0}
              onClick={() => setPage(currentPage - 1)}
              className="grid size-11 place-items-center rounded hover:bg-bg-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet disabled:opacity-30"
            >
              <ChevronLeft className="size-4" aria-hidden="true" />
            </button>
            <span className="tabular-nums">
              {currentPage + 1} / {pages}
            </span>
            <button
              type="button"
              aria-label={t('Page suivante', 'Next page')}
              disabled={currentPage === pages - 1}
              onClick={() => setPage(currentPage + 1)}
              className="grid size-11 place-items-center rounded hover:bg-bg-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet disabled:opacity-30"
            >
              <ChevronRight className="size-4" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function AccountRow({
  account,
  totalViews,
}: {
  account: AnalyticsInsightAccount;
  totalViews: number;
}) {
  const { t } = useI18n();
  return (
    <tr>
      <td className="min-w-0 px-3 py-2.5 sm:px-5">
        <Link
          to={`/accounts/${encodeURIComponent(account.accountId)}`}
          className="flex min-h-11 min-w-0 items-center gap-2 rounded font-medium text-text-1 hover:text-violet-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet"
        >
          <span
            className="hidden size-7 shrink-0 items-center justify-center rounded-lg bg-violet-soft text-[10px] uppercase text-violet-deep sm:flex"
            aria-hidden="true"
          >
            {account.username.slice(0, 2)}
          </span>
          <span className="min-w-0 truncate">@{account.username}</span>
        </Link>
        <p className="text-[11px] leading-4 text-text-3">
          {account.measurable
            ? t(
                `${formatInt(account.measuredReels)} Reels mesurés`,
                `${formatInt(account.measuredReels)} measured Reels`,
              )
            : t('Sans mesures exploitables', 'No usable measurements')}
        </p>
      </td>
      <td
        className="px-2 py-3 text-right font-medium tabular-nums sm:px-5"
        title={account.measurable ? formatInt(account.viewsDelta) : undefined}
      >
        {account.measurable ? formatNumber(account.viewsDelta) : '—'}
      </td>
      <td className="px-3 py-3 sm:px-5">
        <Contribution
          value={account.measurable && totalViews > 0 ? account.viewsDelta / totalViews : null}
        />
      </td>
    </tr>
  );
}

function Contribution({ value }: { value: number | null }) {
  return (
    <div className="flex items-center justify-end gap-2">
      <span
        className="hidden h-1.5 w-16 overflow-hidden rounded-sm bg-bg-3 lg:block"
        aria-hidden="true"
      >
        <span className="block h-full bg-violet" style={{ width: `${(value ?? 0) * 100}%` }} />
      </span>
      <span className="whitespace-nowrap tabular-nums text-text-1">
        {formatPct(value, { sign: false, digits: 1 })}
      </span>
    </div>
  );
}
